import React, {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

export interface CropMode {
  enabled: boolean
  sourceDocumentId?: string
  cropRect?: {
    x: number
    y: number
    width: number
    height: number
  }
  currentPage?: number
  onCropComplete?: (
    rect:
      | {
          x: number
          y: number
          width: number
          height: number
        }
      | undefined
  ) => void
  onPageChange?: (page: number) => void
}

export interface DocumentDrawing {
  zone_reference_image_id: string
  drawing_bounds: {
    x_start: number
    y_start: number
    x_end: number
    y_end: number
  }
}

export interface DocumentRegion extends Partial<DocumentDrawing> {
  id: string
  title: string
  color?: string
  ai_suggested?: boolean
  readOnly?: boolean
  redrawTargetDocumentId?: string
}

export interface DocumentDrawingTarget {
  element: HTMLElement
  documentId: string
  url: string
}

export interface LibraDocumentContextValue {
  selectedDocumentId: string | null
  /**
   * Open a document by Rolodex entry_id, optionally jumping to a specific page.
   * When page is provided the viewer opens at that page instead of page 1.
   */
  setSelectedDocumentId: (documentId: string | null, page?: number) => void
  unitId: string | undefined
  currentViewPage: number
  setCurrentViewPage: (page: number) => void
  /** Increments on each citation-click navigation. Used by EmbeddedDocumentPanel to trigger an imperative scrollToPage call without remounting the viewer. */
  navigationKey: number
  /** Target page for the current citation navigation — stable between scroll events. */
  citationInitialPage: number
  cropMode: CropMode
  setCropMode: React.Dispatch<React.SetStateAction<CropMode>>
  drawingHost?: { owner: symbol; documentId?: string }
  setDrawingHost: (owner: symbol, documentId?: string, claim?: boolean) => void
  unregisterDrawingHost: (owner: symbol) => void
  drawingTarget?: DocumentDrawingTarget
  setDrawingTarget: (
    owner: symbol,
    target: DocumentDrawingTarget,
    claim?: boolean
  ) => void
  unregisterDrawingTarget: (owner: symbol) => void
}

export const LibraDocumentContext = createContext<
  LibraDocumentContextValue | undefined
>(undefined)

export interface LibraDocumentProviderProps {
  children: ReactNode
  unitId: string | undefined
}

export const LibraDocumentProvider: FC<LibraDocumentProviderProps> = ({
  children,
  unitId,
}) => {
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(
    null
  )
  const [currentViewPage, setCurrentViewPageState] = useState<number>(1)
  const [navigationKey, setNavigationKey] = useState<number>(0)
  const [citationInitialPage, setCitationInitialPage] = useState<number>(1)
  const [cropMode, setCropMode] = useState<CropMode>({
    enabled: false,
  })
  const [drawingHost, setDrawingHost] = useState<
    { owner: symbol; documentId?: string } | undefined
  >()
  const [drawingTargetRegistration, setDrawingTargetRegistration] = useState<{
    owner: symbol
    target: DocumentDrawingTarget
  }>()

  const handleSetSelectedDocumentId = useCallback(
    (documentId: string | null, page?: number) => {
      setSelectedDocumentId(documentId)
      setCurrentViewPageState(page ?? 1)
      if (page !== undefined) {
        setCitationInitialPage(page)
        setNavigationKey((k) => k + 1)
      } else {
        setCitationInitialPage(1)
      }
    },
    []
  )

  const handleSetCurrentViewPage = useCallback((page: number) => {
    setCurrentViewPageState(page)
  }, [])

  const setDrawingHostForOwner = useCallback(
    (owner: symbol, documentId?: string, claim = false) => {
      setDrawingHost((current) =>
        claim || current?.owner === owner ? { owner, documentId } : current
      )
    },
    []
  )
  const setDrawingTargetForOwner = useCallback(
    (owner: symbol, target: DocumentDrawingTarget, claim = false) => {
      setDrawingTargetRegistration((current) =>
        claim || current?.owner === owner ? { owner, target } : current
      )
    },
    []
  )
  const unregisterDrawingHost = useCallback((owner: symbol) => {
    setDrawingHost((current) =>
      current?.owner === owner ? undefined : current
    )
  }, [])
  const unregisterDrawingTarget = useCallback((owner: symbol) => {
    setDrawingTargetRegistration((current) =>
      current?.owner === owner ? undefined : current
    )
  }, [])

  const value = useMemo<LibraDocumentContextValue>(
    () => ({
      selectedDocumentId,
      setSelectedDocumentId: handleSetSelectedDocumentId,
      unitId,
      currentViewPage,
      setCurrentViewPage: handleSetCurrentViewPage,
      navigationKey,
      citationInitialPage,
      cropMode,
      setCropMode,
      drawingHost,
      setDrawingHost: setDrawingHostForOwner,
      unregisterDrawingHost,
      drawingTarget: drawingTargetRegistration?.target,
      setDrawingTarget: setDrawingTargetForOwner,
      unregisterDrawingTarget,
    }),
    [
      selectedDocumentId,
      handleSetSelectedDocumentId,
      unitId,
      currentViewPage,
      handleSetCurrentViewPage,
      navigationKey,
      citationInitialPage,
      cropMode,
      drawingHost,
      setDrawingHostForOwner,
      unregisterDrawingHost,
      drawingTargetRegistration,
      setDrawingTargetForOwner,
      unregisterDrawingTarget,
    ]
  )

  return (
    <LibraDocumentContext.Provider value={value}>
      {children}
    </LibraDocumentContext.Provider>
  )
}

export const useLibraDocument = (): LibraDocumentContextValue => {
  const context = useContext(LibraDocumentContext)
  if (!context) {
    throw new Error(
      'useLibraDocument must be used within a LibraDocumentProvider'
    )
  }
  return context
}
